import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import './ProgressCalendar.css';

const ProgressCalendar = ({ userId }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [progressData, setProgressData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchProgressData();
    }
  }, [userId, currentDate]);

  const fetchProgressData = async () => {
    try {
      setLoading(true);
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      const { data: readings, error } = await supabase
        .from('scroll_readings')
        .select('date, scroll_id, reading_number')
        .eq('user_id', userId)
        .gte('date', startOfMonth.toISOString().split('T')[0])
        .lte('date', endOfMonth.toISOString().split('T')[0]);

      if (error) throw error;

      const progress = {};
      readings.forEach(reading => {
        const dateKey = reading.date;
        if (!progress[dateKey]) {
          progress[dateKey] = {
            readings: 0,
            scrolls: new Set()
          };
        }
        progress[dateKey].readings++;
        progress[dateKey].scrolls.add(reading.scroll_id);
      });

      Object.keys(progress).forEach(date => {
        progress[date].scrollCount = progress[date].scrolls.size;
        delete progress[date].scrolls;
      });

      setProgressData(progress);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching progress data:', error);
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return { daysInMonth, startingDayOfWeek };
  };

  const getCompletionLevel = (date) => {
    const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
    const progress = progressData[dateKey];

    if (!progress) return 'none';
    if (progress.readings >= 3) return 'perfect';   // green
    if (progress.readings === 2) return 'partial';  // yellow-green, qualifying day
    if (progress.readings === 1) return 'single';   // yellow, showed up
    return 'none';
  };

  const getIndicatorIcon = (completionLevel) => {
    if (completionLevel === 'perfect') return '✓';
    return null;
  };

  const isToday = (date) => {
    const today = new Date();
    return date === today.getDate() &&
           currentDate.getMonth() === today.getMonth() &&
           currentDate.getFullYear() === today.getFullYear();
  };

  const isFutureDate = (date) => {
    const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), date);
    return checkDate > new Date();
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth();
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const renderCalendarDays = () => {
    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    for (let date = 1; date <= daysInMonth; date++) {
      const completionLevel = getCompletionLevel(date);
      const isCurrentDay = isToday(date);
      const isFuture = isFutureDate(date);
      const icon = getIndicatorIcon(completionLevel);

      days.push(
        <div
          key={date}
          className={`calendar-day ${completionLevel} ${isCurrentDay ? 'today' : ''} ${isFuture ? 'future' : ''}`}
        >
          <span className="day-number">{date}</span>
          {icon && !isFuture && (
            <div className="completion-indicator">{icon}</div>
          )}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="progress-calendar">
      <div className="calendar-header">
        <button onClick={previousMonth} className="nav-button">‹</button>
        <div className="month-title">
          <h2>{monthName}</h2>
          {currentDate.getMonth() !== new Date().getMonth() && (
            <button onClick={goToToday} className="today-button">Today</button>
          )}
        </div>
        <button onClick={nextMonth} className="nav-button">›</button>
      </div>

      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color perfect"></span>
          <span>3 Read — Perfect</span>
        </div>
        <div className="legend-item">
          <span className="legend-color partial"></span>
          <span>2 Read</span>
        </div>
        <div className="legend-item">
          <span className="legend-color single"></span>
          <span>1 Read</span>
        </div>
        <div className="legend-item">
          <span className="legend-color none"></span>
          <span>No activity</span>
        </div>
      </div>

      <div className="calendar-weekdays">
        <div>Sun</div>
        <div>Mon</div>
        <div>Tue</div>
        <div>Wed</div>
        <div>Thu</div>
        <div>Fri</div>
        <div>Sat</div>
      </div>

      {loading ? (
        <div className="calendar-loading">Loading calendar...</div>
      ) : (
        <div className="calendar-grid">
          {renderCalendarDays()}
        </div>
      )}
    </div>
  );
};

export default ProgressCalendar;
